import {useState, useEffect} from 'react';
import api from 'libs/api';

/**
 * Custom hook for PDF generation and export
 * @param {Object} options - Configuration options
 * @param {string} options.templateId - Template ID for PDF generation
 * @param {string} options.templateName - Template name for PDF generation
 * @param {Object} options.data - Data payload for PDF generation
 * @param {string} [options.namePrefix] - Optional prefix for PDF name (e.g., 'pdf-link')
 * @param {string|number} [options.id] - Optional ID to append to PDF name
 * @returns {Object} - { isGenerating, handlePdfExport }
 */
export const usePdfExport = ({templateId, templateName, data, namePrefix = 'pdf-link', id = null}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [localPdfUrl, setLocalPdfUrl] = useState(null);

  // Effect to open PDF URL when it becomes available
  useEffect(() => {
    if (localPdfUrl) {
      window.open(localPdfUrl, '_blank');
      setIsGenerating(false);
      setLocalPdfUrl(null);
    }
  }, [localPdfUrl]);

  // Handler for PDF export
  const handlePdfExport = (e) => {
    e.preventDefault();

    // Prevent multiple clicks
    if (isGenerating) {
      return;
    }

    setIsGenerating(true);

    try {
      const pdfName = id
        ? `${namePrefix}-${templateName}-${id}`
        : `${namePrefix}-${templateName}`;

      const pdfData = {
        templateId,
        name: pdfName,
        data
      };

      api.post(`/pdf/${templateName}`, pdfData)
        .then((responseData) => {
          setLocalPdfUrl(responseData.data.response);
        })
        .catch((error) => {
          console.error('Error generating PDF:', error);
          setIsGenerating(false);
        });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setIsGenerating(false);
    }
  };

  return {isGenerating, handlePdfExport};
};

/**
 * Generating PDF data
 * @param {Object} rowData - The row data containing coverage summary information
 * @returns {Object} - { data }
 */
export const generatePdfData = (rowData) => {
  const {
    fields,
    documents,
  } = rowData;

  // Find the primary coverage document
  const coverageDocument =  documents.find((doc) => {
    return doc.isPrimary === true;
  });

  // Find the other form/document (not primary)
  const paForm = documents.find((doc) => {
    return doc.isPrimary !== true;
  });

  // Create an array of fields data
  let fieldsData = {};
  fields.forEach((field) => {
    fieldsData[field.fieldName] = field.value;
  });

  const isMedical = fieldsData['benefit'] === 'Medical';

  return {
    planType: isMedical ? fieldsData['planType'] : fieldsData['rxPlanType'],
    state: fieldsData['states'] ? fieldsData['states'].join(', ') : null,
    Indication: fieldsData['indication'] ?? null,
    criteria: fieldsData['Coverage Criteria'] ?? null,
    covered: fieldsData['Covered'] ?? null,
    priorAuthorizationRequired: fieldsData['Prior Authorization'] ?? null,
    payer: isMedical ? fieldsData['payer'] : fieldsData['pbmClient'],
    phone: fieldsData['PA Phone Number'] ?? null,
    fax: fieldsData['PA Fax Number'] ?? null,
    lastReviewDate: fieldsData['Last Day Reviewed'] ?? null,
    priorAuthorizationFormUrl: paForm?.source ?? null,
    policyUrl: coverageDocument?.source ?? null,
    benefitType: fieldsData['benefit'] ?? null
  };
}
